/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';

import { useAppDispatch } from '../../app/redux/hooks';
import AppLogger from '../../common/logger/AppLogger';
import { appSubscriptionService } from '../../common/subscriptions/appSubscriptionService';
import { PinEntryDevice, useConfirmEftFailureMutation } from '../api';
import { useOperatorSession } from '../appState';
import { showErrorDialog } from '../error-dialog/errorDialogSlice';

import CardFailureConfirmationAmount from './CardFailureConfirmationAmount';
import CardFailureConfirmationChoosePed from './CardFailureConfirmationChoosePed';
import CardFailureConfirmationFinal from './CardFailureConfirmationFinal';
import './styles.sass';
import CardFailureConfirmationTimedOutExtra from './CardFailureConfirmationTimedOutExtra';

export default function CardFailureConfirmation() {
  const dispatch = useAppDispatch();
  const [state, setState] = useState<any>();
  const [step, setStep] = useState(0);
  const [ped, setPed] = useState<PinEntryDevice | undefined>();
  const [amount, setAmount] = useState(0);
  const operatorSession = useOperatorSession();

  const [triggerConfirmEftFailure] = useConfirmEftFailureMutation();

  useEffect(() => {
    const sub = appSubscriptionService.errorButtonClickedObservable().subscribe((val: any) => {
      if (val && val.type === 'card-failure') {
        setState(val);
        setStep(1);
      }
    });
    return () => sub.unsubscribe();
  }, [setState]);

  const onConfirm = () => {
    if (ped && amount) {
      triggerConfirmEftFailure({
        pinEntryDeviceId: ped?.id ?? -1,
        transactionId: state.transactionId,
        operatorId: operatorSession?.operator.id ?? 0,
        confirmHasFailed: true,
        lastSuccessfulAmount: amount,
        cardRequestId: state.cardRequestId,
      })
        .unwrap()
        .then(() => {
          setState(undefined);
          setStep(0);
          setPed(undefined);
          setAmount(0);
        })
        .catch((err) => {
          AppLogger.error(err);
          dispatch(
            showErrorDialog({
              message: 'Failed to confirm payment',
              dismissible: true,
              buttonText: 'OK',
            }),
          );
        });
    }
  };

  const onCancelWindow = () => {
    setState(undefined);
    setStep(0);
    setPed(undefined);
    setAmount(0);
    const priceFormatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    });

    dispatch(
      showErrorDialog({
        errorType: 'card-payment-failure',
        message: state.message,
        dismissible: true,
        buttonText: 'Payment Failed',
        backgroundColor: 'red',
        alternateButtonText: 'Payment Completed',
        showAlternateButton: true,
        requireConfirmationOnClose: true,
        confirmNoText: 'Back',
        confirmYesText: 'Confirm Failed',
        confirmAlternateNoText: 'Back',
        confirmAlternateYesText: 'Enter Auth Code',
        confirmationText: `Are you sure the payment failed?<br /><br /> Please confirm you checked the ${
          state.pedName
        } card machine and have proof the payment declined for ${priceFormatter.format(amount)}`,
        confirmationTextAlternate: `Please confirm the payment succeeded,<br /><br /> You will need to enter the Auth Code off the card machine.<br /><br /><b>Amount:</b> ${priceFormatter.format(
          amount,
        )}<br /><b>Card Machine:</b> ${state.pedName}`,
        state: {
          transactionId: state.transactionId,
          transactionNumber: state.transactionNumber,
          sequenceNumber: state.sequenceNumber,
          operatorName: state.operatorName,
          operatorId: state.operatorId,
          amount: state.amount,
          reason: state.reason,
          cardRequestId: state.cardRequestId,
          message: state.message,
          pedName: state.pedName,
          pedTerminalId: state.pedTerminalId,
          type: 'card-failure',
        },
        alternateState: {
          transactionId: state.transactionId,
          transactionNumber: state.transactionNumber,
          sequenceNumber: state.sequenceNumber,
          operatorName: state.operatorName,
          amount: state.amount,
          reason: state.reason,
          cardRequestId: state.cardRequestId,
          operatorId: state.operatorId,
          message: state.message,
          pedName: state.pedName,
          pedTerminalId: state.pedTerminalId,
          type: 'offline-card',
        },
      }),
    );
    setState(undefined);
  };

  if (state == undefined) return <></>;

  return (
    <div className="pos-modal-portal">
      <div
        className="pos-modal-overlay"
        onClick={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((e.target as any).className === 'pos-modal-overlay') onCancelWindow();
        }}
      >
        {step === 1 && (
          <CardFailureConfirmationChoosePed
            selectedPed={(pedGot) => {
              if (pedGot) setPed(pedGot);

              setStep(2);
            }}
            txPedTerminalId={state.pedTerminalId}
          />
        )}
        {step === 2 && (
          <CardFailureConfirmationTimedOutExtra
            pedName={state.pedName}
            onTimeOutConfirmed={(confirmed) => {
              if (confirmed) setStep(3);
              else onCancelWindow();
            }}
            originalAmount={state.amount}
          />
        )}
        {step === 3 && (
          <CardFailureConfirmationAmount
            onAmountConfirmed={(amount: number) => {
              setAmount(amount);
              setStep(4);
            }}
          />
        )}
        {step === 4 && ped && <CardFailureConfirmationFinal onClick={onConfirm} amount={amount} ped={ped} />}
      </div>
    </div>
  );
}
